/**
 * The letter and the report, client side (plan section 7).
 *
 * Its own file rather than more of `siteBrain.ts`: that one is about running a
 * brain and reading what it found, this one is about an outbound message and a
 * legal gate, and the second is the half where a mistake reaches a stranger.
 *
 * ONE RULE TRAVELS WITH THESE TYPES. There is no call here that sends a
 * recipient to the server. `to` only ever comes back FROM the API, decided by
 * the legal gate, on every hand-over. A client that could name the recipient
 * would move the gate into the browser, where it is one refactor from not
 * running at all - and the failure would be invisible, because a letter to the
 * wrong person looks exactly like a letter to the right one.
 */
import { api } from "./api";
import { brainKey } from "./siteBrain";

/**
 * How lawful this recipient is.
 *
 * `blocked` is the only value with no draft behind it. The other four all send;
 * they differ in what the letter has to carry, which the server appends.
 */
export type BrainLegalState =
  | "register_legal"
  | "register_personal"
  | "scraped_legal"
  | "no_address"
  | "blocked";

export type BrainLegalReason =
  | "register_contact"
  | "register_personal_mailbox"
  | "site_role_account"
  | "no_address_known"
  | "natural_person"
  | "sole_trader"
  | "suppressed";

/** What the letter asks for. Stored, so a month of sending can be counted. */
export type BrainCtaVariant = "call" | "demo" | "report";

export interface BrainLegal {
  state: BrainLegalState;
  reason: BrainLegalReason;
  /** Null whenever the gate withholds it. Never filled in by this app. */
  address: string | null;
  needsPrivacyNotice: boolean;
}

export interface BrainSuppression {
  kind: "email" | "domain";
  value: string;
  source: string;
  at: string;
}

export interface BrainLegalView extends BrainLegal {
  domain: string;
  /** The row behind a `suppressed` refusal, so the card can say when and why. */
  suppression: BrainSuppression | null;
}

/** One proof, re-requested before the letter is offered. */
export interface BrainFreshnessCheck {
  findingId: string;
  url: string | null;
  verdict: "fresh" | "stale" | "unchecked";
  detail: string | null;
}

export interface BrainFreshness {
  at: string;
  checks: BrainFreshnessCheck[];
  /** Findings whose proof no longer holds. Non-empty blocks the prefill. */
  stale: string[];
  synthesisStale: boolean;
}

export interface BrainEmailDraft {
  runSeq: number;
  to: string | null;
  subject: string;
  body: string;
  legalState: BrainLegalState;
  legalReason: BrainLegalReason;
  variant: BrainCtaVariant;
  findingIds: string[];
  generatedAt: string;
  editedAt?: string;
  model: string;
  sentAt?: string;
  emailMessageId?: string;
}

export interface BrainEmailResult {
  ok: boolean;
  draft: BrainEmailDraft;
  legal: BrainLegal;
  freshness?: BrainFreshness;
}

/** The refusal bodies, which carry more than a message. */
export interface BrainEmailRefusal {
  error: "no_synthesis" | "blocked" | "stale_evidence" | "pass_failed";
  detail?: string;
  legal: BrainLegal;
  freshness?: BrainFreshness;
}

export interface BrainReportResult {
  ok: boolean;
  domain: string;
  key: string;
  token: string;
  builtAt: string;
  findings: number;
  shots: number;
  /** Path on this API. Made absolute by the card before it is copied. */
  url: string;
}

const key = (domain: string): string => encodeURIComponent(brainKey(domain));

/** May we write to this company, and if not, why not. Safe to ask on card open. */
export const getBrainLegal = (domain: string): Promise<BrainLegalView> =>
  api.get<BrainLegalView>(`/admin/ariregister/brain/${key(domain)}/legal`);

/**
 * Write the draft.
 *
 * `force` is the operator confirming an old verdict or a stale proof, and is
 * never a default. Even forced, the server drops arguments whose evidence has
 * gone stale: confirming is permission to send an older letter, not permission
 * to put a disproven claim in it.
 */
export const generateBrainEmail = (
  domain: string,
  variant: BrainCtaVariant,
  force = false,
): Promise<BrainEmailResult> =>
  api.post<BrainEmailResult>(`/admin/ariregister/brain/${key(domain)}/email`, { variant, force });

/** What to open the composer with. 404 means "press the button first". */
export const getBrainEmailDraft = (domain: string): Promise<BrainEmailDraft & { warningKey?: string }> =>
  api.get<BrainEmailDraft & { warningKey?: string }>(`/admin/ariregister/brain/${key(domain)}/email-draft`);

/** Stamp the draft as sent. Best effort: never blocks the operator. */
export const markBrainEmailSent = (domain: string, emailMessageId?: string): Promise<{ ok: boolean; stamped: boolean }> =>
  api.post<{ ok: boolean; stamped: boolean }>(`/admin/ariregister/brain/${key(domain)}/email/sent`, { emailMessageId });

/** They said no. Writes the marketing module's own suppression row. */
export const suppressBrainCompany = (domain: string, note?: string): Promise<{ ok: boolean; written: number }> =>
  api.post<{ ok: boolean; written: number }>(`/admin/ariregister/brain/${key(domain)}/suppress`, { note });

/** Build the sendable page. A rebuild issues a new link and resets the count. */
export const buildBrainReport = (domain: string): Promise<BrainReportResult> =>
  api.post<BrainReportResult>(`/admin/ariregister/brain/${key(domain)}/report`, {});
